import React from 'react';
import { Link } from 'react-router-dom';
import { InnerLayout, PillNavBar } from '../components/layout/InnerLayout';
import { Button } from '../components/ui/Button';
import { HedgehogSpinner } from '../components/brand/HedgehogSpinner';
import { FolderKanban, Users, Trash2, AlertTriangle, Sliders } from 'lucide-react';
import { useAdminOrgData } from './admin/organization/useAdminOrgData';
import { AdminOrgHeader } from './admin/organization/components/AdminOrgHeader';
import { EditQuotaDrawer } from './admin/organization/components/EditQuotaDrawer';
import { AdminOrgProjectsTab } from './admin/organization/AdminOrgProjectsTab';
import { AdminOrgMembersTab } from './admin/organization/AdminOrgMembersTab';
import { AdminOrgQuotasTab } from './admin/organization/AdminOrgQuotasTab';
import { AdminOrgDangerTab } from './admin/organization/AdminOrgDangerTab';

export const AdminOrganizationDetailView: React.FC = () => {
  const data = useAdminOrgData();

  if (data.isLoading || !data.organization) {
    return (
      <div className="flex items-center justify-center h-96">
        <HedgehogSpinner size="lg" label="LOADING ORGANIZATION..." />
      </div>
    );
  }

  const { organization, projects, members, activeTab, setActiveTab } = data;
  const totalDatastores = projects.reduce((acc, p) => acc + (p.table_count || 0), 0);
  const totalSubmissions = projects.reduce((acc, p) => acc + (p.submission_count || 0), 0);
  const totalStorageBytes = projects.reduce((acc, p) => acc + (p.storage_bytes || 0), 0);

  return (
    <InnerLayout
      title={`Organization: ${organization.name}`}
      description="Superadmin inspection and management console for organization, projects, and datastores."
      variant="default"
      actions={[
        {
          label: 'Edit Quotas & Tier',
          icon: <Sliders size={14} className="text-cf-orange" />,
          variant: 'secondary',
          onClick: () => data.setEditQuotaDrawerOpen(true),
        },
        {
          label: 'Delete Organization',
          icon: Trash2,
          variant: 'danger',
          onClick: () => setActiveTab('danger'),
        },
      ]}
    >
      <div className="space-y-6 pb-16 font-sans text-xs">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-cf-textSecondary">
          <Link to="/admin/organizations" className="hover:text-cf-orange transition-colors">
            Organizations
          </Link>
          <span>/</span>
          <span className="text-cf-textPrimary font-semibold">{organization.name}</span>
        </div>

        {/* Header: Identity Card & Bento Stats */}
        <AdminOrgHeader
          organization={organization}
          projects={projects}
          totalDatastores={totalDatastores}
          totalSubmissions={totalSubmissions}
          totalStorageBytes={totalStorageBytes}
          copiedId={data.copiedId}
          onCopyId={data.handleCopyId}
        />

        {/* Tab Navigation */}
        <PillNavBar
          items={[
            {
              id: 'projects',
              label: `Projects & Datastores (${projects.length})`,
              icon: FolderKanban,
              active: activeTab === 'projects',
              onClick: () => setActiveTab('projects'),
            },
            {
              id: 'members',
              label: `Members & Roles (${members.length})`,
              icon: Users,
              active: activeTab === 'members',
              onClick: () => setActiveTab('members'),
            },
            {
              id: 'quotas',
              label: 'Quotas & Ingestion Limits',
              icon: Sliders,
              active: activeTab === 'quotas',
              onClick: () => setActiveTab('quotas'),
            },
            {
              id: 'danger',
              label: 'Danger Zone',
              icon: AlertTriangle,
              danger: true,
              active: activeTab === 'danger',
              onClick: () => setActiveTab('danger'),
            },
          ]}
        />

        {/* Tab Content */}
        {activeTab === 'projects' && (
          <AdminOrgProjectsTab
            projects={projects}
            projectTables={data.projectTables}
            expandedProjects={data.expandedProjects}
            loadingTables={data.loadingTables}
            copiedToken={data.copiedToken}
            projectToDelete={data.projectToDelete}
            projectDeleteInput={data.projectDeleteInput}
            isDeletingProject={data.isDeletingProject}
            tableToDelete={data.tableToDelete}
            tableDeleteInput={data.tableDeleteInput}
            isDeletingTable={data.isDeletingTable}
            onToggleProjectExpansion={data.toggleProjectExpansion}
            onCopyToken={data.handleCopyToken}
            onSelectProjectToDelete={data.setProjectToDelete}
            setProjectDeleteInput={data.setProjectDeleteInput}
            onConfirmDeleteProject={data.handleConfirmDeleteProject}
            onSelectTableToDelete={data.setTableToDelete}
            setTableDeleteInput={data.setTableDeleteInput}
            onConfirmDeleteTable={data.handleConfirmDeleteTable}
          />
        )}

        {activeTab === 'members' && (
          <AdminOrgMembersTab
            members={members}
            onRemoveMember={data.handleRemoveMember}
          />
        )}

        {activeTab === 'quotas' && (
          <AdminOrgQuotasTab
            organization={organization}
            members={members}
            totalDatastores={totalDatastores}
            onOpenEditQuotaDrawer={() => data.setEditQuotaDrawerOpen(true)}
          />
        )}

        {activeTab === 'danger' && (
          <AdminOrgDangerTab
            organization={organization}
            projects={projects}
            totalDatastores={totalDatastores}
            totalSubmissions={totalSubmissions}
            orgDeleteInput={data.orgDeleteInput}
            setOrgDeleteInput={data.setOrgDeleteInput}
            isDeletingOrg={data.isDeletingOrg}
            onConfirmDeleteOrg={data.handleConfirmDeleteOrg}
          />
        )}
      </div>

      {/* Edit Quota & Tier Slide-Over Right Drawer */}
      <EditQuotaDrawer
        isOpen={data.editQuotaDrawerOpen}
        onClose={() => data.setEditQuotaDrawerOpen(false)}
        organization={organization}
        plans={data.plans}
        editTier={data.editTier}
        editMaxSubmissions={data.editMaxSubmissions}
        setEditMaxSubmissions={data.setEditMaxSubmissions}
        editMaxTables={data.editMaxTables}
        setEditMaxTables={data.setEditMaxTables}
        editMaxSeats={data.editMaxSeats}
        setEditMaxSeats={data.setEditMaxSeats}
        isUpdatingQuotas={data.isUpdatingQuotas}
        onTierSelect={data.handleTierSelect}
        onSaveQuotas={data.handleSaveQuotas}
      />
    </InnerLayout>
  );
};
